import React from 'react';
import { Search, Filter, X, Mail } from 'lucide-react';
import type { OrderIssueFilters, OrderIssueType, OrderIssueStatus } from '../../types/orderIssues';

interface IssueFilterBarProps {
  filters: OrderIssueFilters;
  onUpdateFilters: (filters: Partial<OrderIssueFilters>) => void;
  selectedCount: number;
  onBulkResolve: () => void;
  onBulkIgnore: () => void;
  onBulkEmail?: () => void;
}

const ISSUE_TYPE_OPTIONS: { value: OrderIssueType | 'all'; label: string }[] = [
  { value: 'all', label: 'All Types' },
  { value: 'label_generation_failed', label: 'Label Failed' },
  { value: 'address_validation_error', label: 'Address Error' },
  { value: 'postnl_api_error', label: 'PostNL Error' },
  { value: 'shopify_fulfillment_error', label: 'Shopify Error' },
  { value: 'incomplete_address', label: 'Incomplete Address' },
  { value: 'postal_code_invalid', label: 'Postal Code Invalid' },
  { value: 'other', label: 'Other' },
];

const STATUS_OPTIONS: { value: OrderIssueStatus | 'all'; label: string }[] = [
  { value: 'open', label: 'Open' },
  { value: 'under_process', label: 'Under Process' },
  { value: 'processed', label: 'Processed' },
  { value: 'resolved', label: 'Resolved' },
  { value: 'ignored', label: 'Ignored' },
  { value: 'all', label: 'All' },
];

export const IssueFilterBar: React.FC<IssueFilterBarProps> = ({
  filters,
  onUpdateFilters,
  selectedCount,
  onBulkResolve,
  onBulkIgnore,
  onBulkEmail,
}) => {
  return (
    <div className="space-y-3">
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search by order, customer, or error..."
            value={filters.search}
            onChange={(e) => onUpdateFilters({ search: e.target.value })}
            className="w-full pl-10 pr-8 py-2.5 border border-gray-200 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 transition-all"
          />
          {filters.search && (
            <button
              onClick={() => onUpdateFilters({ search: '' })}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        <div className="flex gap-2">
          <div className="relative">
            <Filter className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
            <select
              value={filters.issueType}
              onChange={(e) => onUpdateFilters({ issueType: e.target.value as OrderIssueType | 'all' })}
              className="pl-9 pr-8 py-2.5 border border-gray-200 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 appearance-none cursor-pointer"
            >
              {ISSUE_TYPE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          <div className="relative">
            <select
              value={filters.status}
              onChange={(e) => onUpdateFilters({ status: e.target.value as OrderIssueStatus | 'all' })}
              className="px-4 py-2.5 border border-gray-200 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 appearance-none cursor-pointer"
            >
              {STATUS_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {selectedCount > 0 && (
        <div className="flex items-center gap-3 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800/40 rounded-xl">
          <span className="text-sm font-medium text-amber-700 dark:text-amber-300">
            {selectedCount} selected
          </span>
          {onBulkEmail && (
            <button
              onClick={onBulkEmail}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-blue-700 bg-blue-100 hover:bg-blue-200 dark:text-blue-300 dark:bg-blue-900/30 dark:hover:bg-blue-900/50 rounded-lg transition-colors"
            >
              <Mail className="h-3.5 w-3.5" />
              Email Selected
            </button>
          )}
          <button
            onClick={onBulkResolve}
            className="px-3 py-1.5 text-xs font-medium text-emerald-700 bg-emerald-100 hover:bg-emerald-200 dark:text-emerald-300 dark:bg-emerald-900/30 dark:hover:bg-emerald-900/50 rounded-lg transition-colors"
          >
            Resolve Selected
          </button>
          <button
            onClick={onBulkIgnore}
            className="px-3 py-1.5 text-xs font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 dark:text-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 rounded-lg transition-colors"
          >
            Ignore Selected
          </button>
        </div>
      )}
    </div>
  );
};
